import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { AdvertiserUser } from '../../types/user';

interface AuthState {
  isAuthenticated: boolean;
  user: AdvertiserUser | null;
  accessToken: string | null;
  hasExited: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  hasExited: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    registerSuccess: (state, action: PayloadAction<{ user: AdvertiserUser; accessToken?: string }>) => {
      state.user = action.payload.user;
      // Allow dashboard access immediately after registration
      // User will see "pending verification" banner but can explore/setup campaigns
      state.isAuthenticated = true;
      if (action.payload.accessToken) {
        state.accessToken = action.payload.accessToken;
      }
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: AdvertiserUser;
        accessToken: string;
      }>
    ) => {
      const { user, accessToken } = action.payload;
      state.isAuthenticated = true;
      state.user = user;
      state.accessToken = accessToken;
      Sentry.setUser({ id: user.id, email: user.email });
    },
    updateAccessToken: (
      state,
      action: PayloadAction<string | null>
    ) => {
      if (action.payload) {
        state.isAuthenticated = true;
        state.accessToken = action.payload;
      } else {
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
      }
    },
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      Sentry.setUser(null);
    },
    setHasExited: (state, action: PayloadAction<boolean>) => {
      state.hasExited = action.payload;
    },
    setBillingSummary: (
      state,
      action: PayloadAction<{
        billing_status: string;
        billing_threshold: number;
        current_unbilled_spend: number;
      }>
    ) => {
      if (!state.user) return;
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    updateUser: (state, action: PayloadAction<Partial<AdvertiserUser>>) => {
      if (!state.user) return;
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
  },
});

export const {
  registerSuccess,
  loginSuccess,
  logoutSuccess,
  updateAccessToken,
  setHasExited,
  setBillingSummary,
  updateUser,
} = authSlice.actions;

export default authSlice.reducer;
